/**
 * Geolocation Service
 *
 * Single source of truth for all browser geolocation API operations.
 * Provides promise-based interface with standardized error handling.
 */

import type { Coordinates } from '../../types';
import {
  GEOLOCATION_HIGH_ACCURACY,
  GEOLOCATION_TIMEOUT_MS,
  GEOLOCATION_MAX_AGE_SHORT_MS,
} from '../../constants/geolocation';

/**
 * Geolocation error codes (standardized)
 */
export type GeolocationErrorCode =
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'NOT_SUPPORTED';

/**
 * Geolocation options for getCurrentPosition
 */
export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  onProgress?: (state: 'requesting' | 'waiting' | 'success' | 'error') => void;
}

/**
 * Result returned from geolocation operations
 */
export interface GeolocationResult {
  success: boolean;
  coordinates?: Coordinates;
  errorCode?: GeolocationErrorCode;
  /** Device heading in degrees (0–360, clockwise from north); null when unavailable. */
  heading?: number | null;
  /** Position accuracy radius in meters (from `position.coords.accuracy`). */
  accuracy?: number;
}

/**
 * Default geolocation options (WGS84 standard for Poland)
 */
const DEFAULT_OPTIONS: Required<Omit<GeolocationOptions, 'onProgress'>> = {
  enableHighAccuracy: GEOLOCATION_HIGH_ACCURACY,
  timeout: GEOLOCATION_TIMEOUT_MS,
  maximumAge: GEOLOCATION_MAX_AGE_SHORT_MS,
};

/**
 * Geolocation Service API
 *
 * Centralized service for all GPS/geolocation operations in the application.
 */
export const geolocationService = {
  /**
   * Check if geolocation is supported by the browser
   *
   * @returns true if geolocation is available
   *
   * @example
   * if (geolocationService.isSupported()) {
   *   const result = await geolocationService.getCurrentPosition();
   * }
   */
  isSupported: (): boolean => {
    return 'geolocation' in navigator;
  },

  /**
   * Get current GPS position with promise-based API
   *
   * @param options - Geolocation options (optional)
   * @returns Promise with GeolocationResult
   *
   * @example
   * const result = await geolocationService.getCurrentPosition({
   *   enableHighAccuracy: true,
   *   timeout: 5000,
   *   onProgress: (state) => console.log('GPS state:', state)
   * });
   *
   * if (result.success) {
   *   console.log('Coordinates:', result.coordinates);
   * } else {
   *   console.error('Error code:', result.errorCode);
   * }
   */
  getCurrentPosition: async (
    options: GeolocationOptions = {}
  ): Promise<GeolocationResult> => {
    if (!geolocationService.isSupported()) {
      return {
        success: false,
        errorCode: 'NOT_SUPPORTED',
      };
    }

    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    options.onProgress?.('requesting');

    try {
      options.onProgress?.('waiting');

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: mergedOptions.enableHighAccuracy,
            timeout: mergedOptions.timeout,
            maximumAge: mergedOptions.maximumAge,
          });
        }
      );

      options.onProgress?.('success');

      return {
        success: true,
        coordinates: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
        // `coords.heading` is `NaN` (not null) when the device is stationary
        // or cannot compute direction. Normalize to `null` so downstream
        // nullish handling (`?? undefined`) works and no `NaN` leaks out.
        heading: Number.isFinite(position.coords.heading)
          ? position.coords.heading
          : null,
        accuracy: position.coords.accuracy,
      };
    } catch (error) {
      options.onProgress?.('error');

      if (error instanceof GeolocationPositionError) {
        return {
          success: false,
          errorCode: geolocationService.getErrorCode(error.code),
        };
      }

      return {
        success: false,
        errorCode: 'POSITION_UNAVAILABLE',
      };
    }
  },

  /**
   * Get standardized error code from GeolocationPositionError code
   *
   * @param code - Native GeolocationPositionError code
   * @returns Standardized error code
   */
  getErrorCode: (code: number): GeolocationErrorCode => {
    switch (code) {
      case GeolocationPositionError.PERMISSION_DENIED:
        return 'PERMISSION_DENIED';
      case GeolocationPositionError.POSITION_UNAVAILABLE:
        return 'POSITION_UNAVAILABLE';
      case GeolocationPositionError.TIMEOUT:
        return 'TIMEOUT';
      default:
        return 'POSITION_UNAVAILABLE';
    }
  },

  /**
   * Check geolocation permission status
   *
   * @returns Permission state or null if Permission API not supported
   *
   * @example
   * const permission = await geolocationService.checkPermission();
   * if (permission === 'granted') {
   *   // GPS is allowed
   * } else if (permission === 'denied') {
   *   // GPS is blocked
   * }
   */
  checkPermission: async (): Promise<PermissionState | null> => {
    if (!navigator.permissions) {
      return null;
    }

    try {
      const permission = await navigator.permissions.query({
        name: 'geolocation',
      });
      return permission.state;
    } catch {
      // Permission API query failed or not supported
      return null;
    }
  },

  /**
   * Watch geolocation permission changes via the Permissions API.
   *
   * Reports the INITIAL permission state, then re-reports on every change.
   * When the Permissions API is unsupported, `onChange` is never called and
   * the returned cleanup is a no-op.
   *
   * @param onChange - Callback invoked with the current permission state
   * @returns Unsubscribe function that clears the change handler
   *
   * @example
   * const unsubscribe = geolocationService.watchPermission((state) => {
   *   console.log('Permission is now:', state);
   * });
   * // Later: stop listening
   * unsubscribe();
   */
  watchPermission: (
    onChange: (state: PermissionState) => void
  ): (() => void) => {
    if (!navigator.permissions) {
      return () => {};
    }

    let isCancelled = false;
    let activeStatus: PermissionStatus | null = null;

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        if (isCancelled) {
          // Unsubscribed before the query resolved — do not attach a handler.
          return;
        }
        activeStatus = status;
        onChange(status.state);
        status.onchange = () => onChange(status.state);
      })
      .catch(() => {
        // Permissions API query failed or unsupported — no updates delivered.
      });

    return () => {
      isCancelled = true;
      if (activeStatus) {
        activeStatus.onchange = null;
      }
    };
  },

  /**
   * Watch position with continuous updates (advanced usage)
   *
   * @param onSuccess - Callback for successful position updates
   * @param onError - Callback for errors
   * @param options - Geolocation options (optional)
   * @returns Watch ID (use with clearWatch to stop)
   *
   * @example
   * const watchId = geolocationService.watchPosition(
   *   (coords) => console.log('Position:', coords),
   *   (error) => console.error('Error:', error),
   *   { enableHighAccuracy: true }
   * );
   *
   * // Later: stop watching
   * geolocationService.clearWatch(watchId);
   */
  watchPosition: (
    onSuccess: (location: {
      lat: number;
      lng: number;
      heading?: number | null;
      accuracy?: number;
    }) => void,
    onError: (errorCode: GeolocationErrorCode) => void,
    options: GeolocationOptions = {}
  ): number | null => {
    if (!geolocationService.isSupported()) {
      onError('NOT_SUPPORTED');
      return null;
    }

    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

    return navigator.geolocation.watchPosition(
      (position) => {
        onSuccess({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          // `coords.heading` is `NaN` (not null) when stationary; normalize to
          // `null` so the consumer's `?? undefined` handling never sees `NaN`.
          heading: Number.isFinite(position.coords.heading)
            ? position.coords.heading
            : null,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        onError(geolocationService.getErrorCode(error.code));
      },
      {
        enableHighAccuracy: mergedOptions.enableHighAccuracy,
        timeout: mergedOptions.timeout,
        maximumAge: mergedOptions.maximumAge,
      }
    );
  },

  /**
   * Stop watching position (cleanup)
   *
   * @param watchId - Watch ID returned from watchPosition
   *
   * @example
   * const watchId = geolocationService.watchPosition(...);
   * // ... later
   * geolocationService.clearWatch(watchId);
   */
  clearWatch: (watchId: number | null): void => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
    }
  },
};
